import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CNavItem,
} from "@coreui/react";
import { TagIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDebounce } from "usehooks-ts";
import { DASHBOARD_ROUTES } from "../../config/routes";
import { useAuth } from "../../hooks/useAuth";
import { SidebarIcon } from "../icons/SidebarIcons";
import { ROLES } from "../../config/constants";
import { useSelectedPlace } from "../../hooks/place/useSelectedPlace";
import {
  PartnerRequestBadge,
  usePartnerRequestAlerts,
} from "../../hooks/Partners/usePartnerRequestAlerts";
import { NewDataBadge, useNewDataAlerts } from "../../hooks/useNewDataAlerts";

const createDashboardRoutes = (
  user: User | null,
  isAdmin: boolean,
  isEventsEnabled: boolean,
) => {
  if (
    user?.role?.label?.toLowerCase() === ROLES.USER.toLowerCase() &&
    user?.isPlaceManager === true
  ) {
    return [
      {
        label: "Activités",
        path: "/place-manager/activities",
        icon: SidebarIcon.ActivitiesSVG,
        show: true,
        isActive: (pathname: string) =>
          pathname.includes("/place-manager/activities"),
      },
      {
        label: "Reservations",
        path: "/place-manager/bookings",
        icon: SidebarIcon.BookingSvg,
        show: true,
        isActive: (pathname: string) =>
          pathname.includes("/place-manager/bookings"),
      },
      {
        label: "Commission",
        path: "/place-manager/commission",
        icon: SidebarIcon.OffersSvg,
        show: true,
        isActive: (pathname: string) =>
          pathname.includes("/place-manager/commission"),
      },
    ];
  }
  if (
    user?.role?.label?.toLowerCase() === ROLES.PLACE_ADMIN.toLowerCase() &&
    user?.isMultiPlaceAdmin === true
  ) {
    return [
      {
        label: "Reservations",
        path: "/bookings",
        icon: SidebarIcon.BookingSvg,
        show: true,
        isActive: (pathname: string) => pathname.includes("/bookings"),
      },

      {
        label: "Offres",
        path: "/offers",
        icon: SidebarIcon.OffersSvg,
        show: true,
        isActive: (pathname: string) => pathname.includes("/offers"),
      },
      {
        label: "Mes Partenaires",
        path: "/place-admin-partners",
        icon: SidebarIcon.PartnersSvg,
        show: true,
        isActive: (pathname: string) =>
          pathname.includes("/place-admin-partners"),
      },
      {
        label: "Établissement",
        path: "/places",
        icon: SidebarIcon.PlaceSVG,
        show: true,
        isActive: (pathname: string) => /^\/places\/?$/.test(pathname),
      },
      {
        label: "Inspecteurs",
        path: DASHBOARD_ROUTES.places.inspectors(user?.adminForPlace?.id ?? ""),
        icon: SidebarIcon.InspectorsSvg,
        show: true,
        isActive: (pathname: string) => pathname.includes("/inspectors"),
      },
      {
        label: "Factures",
        path: DASHBOARD_ROUTES.invoices.list(),
        icon: SidebarIcon.MoneyIcon,
        show: true,
        isActive: (pathname: string) => pathname.includes("/invoices"),
      },
    ];
  }
  return [
    // {
    //   label: "Accueil",
    //   path: "/home",
    //   icon: SidebarIcon.HomeSvg,
    //   show: true,
    //   isActive: (pathname: string) => pathname.includes("/home"),
    // },
    {
      label: "Établissements",
      path: "/places",
      icon: SidebarIcon.PlaceSVG,
      show: true,
      isActive: (pathname: string) => pathname.includes("/places"),
    },
    {
      label: "Utilisateurs",
      path: "/users",
      icon: SidebarIcon.UsersSvg,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/users"),
    },
    {
      label: "Réservations",
      path: "/bookings",
      icon: SidebarIcon.BookingSvg,
      show: user?.adminForPlace?.supportReservations || isAdmin,
      isActive: (pathname: string) => pathname.includes("/bookings"),
    },
    {
      label: "Evénements",
      path: "/events",
      icon: SidebarIcon.EventSvg,
      show: isAdmin || isEventsEnabled,
      isActive: (pathname: string) => pathname.includes("/events"),
    },
    {
      label: "Offres",
      path: "/offers",
      icon: SidebarIcon.OffersSvg,
      show: true,
      isActive: (pathname: string) => pathname.includes("/offers"),
    },
    {
      label: "Activités",
      path: "/activities",
      icon: SidebarIcon.ActivitiesSVG,
      show: true,
      isActive: (pathname: string) => pathname.includes("/activities"),
    },
    {
      label: "Packs",
      path: "/packs",
      icon: SidebarIcon.PacksSvg,
      show: true,
      isActive: (pathname: string) => pathname.includes("/packs"),
    },
    {
      label: "Catégories",
      path: "/categories",
      icon: SidebarIcon.CategoriesSvg,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/categories"),
    },
    {
      label: "Catégories de services",
      path: "/service-categories",
      icon: SidebarIcon.ServiceCategoriesSVG,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/service-categories"),
    },
    {
      label: "Promo codes",
      path: "/promo-codes",
      icon: TagIcon,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/promo-codes"),
    },
    {
      label: "Bonus",
      path: DASHBOARD_ROUTES.reward.list(),
      icon: SidebarIcon.RewardIcon,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/reward"),
    },
    {
      label: "Publicités",
      path: "/advertisement",
      icon: SidebarIcon.AdvertisementSVG,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/advertisement"),
    },
    {
      label: "Notifications",
      path: "/notifications",
      icon: SidebarIcon.NotificationsSvg,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/notifications"),
    },
    // {
    //   label: "Messages de WhatsApp",
    //   path: "/whatsapp-messages",
    //   icon: SidebarIcon.MessagesWhatsappSvg,
    //   show: isAdmin,
    //   isActive: (pathname: string) => pathname.includes("/whatsapp-messages")
    // },
    {
      label: "Partenaires",
      path: "/partners",
      icon: SidebarIcon.PartnersSvg,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/partners"),
    },
    {
      label: "Villes",
      path: "/cities",
      icon: SidebarIcon.CitiesSvg,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/cities"),
    },
    {
      label: "Inspecteurs",
      path: "/inspectors",
      icon: SidebarIcon.InspectorsSvg,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/inspectors"),
    },
    {
      label: "Newsletter",
      path: "/newsletter",
      icon: SidebarIcon.NewsletterSvg,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/newsletter"),
    },
    {
      label: "Variables",
      path: "/variables",
      icon: SidebarIcon.ArrowsRightLeftIcon,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/variables"),
    },
    {
      label: "Paiements",
      path: "/payouts",
      icon: SidebarIcon.PayoutsSvg,
      show: true,
      isActive: (pathname: string) => pathname.includes("/payouts"),
    },
    {
      label: "Traductions",
      path: "/translations",
      icon: SidebarIcon.TranslationsSvg,
      show: isAdmin,
      isActive: (pathname: string) => pathname.includes("/translations"),
    },
  ];
};

const createVenezTaxiRoutes = (user: User | null, isAdmin: boolean) => [
  {
    label: "Chauffeurs",
    path: "/drivers",
    icon: SidebarIcon.DriversSvg,
    show: isAdmin,
    isActive: (pathname: string) => pathname.includes("/drivers"),
  },
  {
    label: "Sessions",
    path: "/Sessions",
    icon: SidebarIcon.SessionSVG,
    show: isAdmin,
    isActive: (pathname: string) => pathname.includes("/Sessions"),
  },
  {
    label: "Demandes",
    path: "/demandes",
    icon: SidebarIcon.RequestDriverSVG,
    show: isAdmin,
    isActive: (pathname: string) => pathname.includes("/demandes"),
  },
  {
    label: "Trajets",
    path: "/rides",
    icon: SidebarIcon.RidesSVG,
    show: isAdmin,
    isActive: (pathname: string) => pathname.includes("/rides"),
  },
  {
    label: "Taxis",
    path: "/taxi",
    icon: SidebarIcon.TaxisSVG,
    show: isAdmin,
    isActive: (pathname: string) => pathname === "/taxi",
  },
  {
    label: "Zones spéciales",
    path: "/taxi/special-zones",
    icon: SidebarIcon.SpecialZoneSvg,
    show: isAdmin,
    isActive: (pathname: string) => pathname.includes("/special-zones"),
  },
  {
    label: "Transactions",
    path: "/transactions",
    icon: SidebarIcon.ArrowsRightLeftIcon,
    show: isAdmin,
    isActive: (pathname: string) => pathname.includes("/transactions"),
  },
  {
    label: "Coupons",
    path: DASHBOARD_ROUTES.taxi.discounts.list(),
    icon: SidebarIcon.OffersSvg,
    show: isAdmin,
    isActive: (pathname: string) => pathname.includes("/taxi/discounts"),
  },
  {
    label: "Statistiques",
    path: "/statistics",
    icon: SidebarIcon.SatistiquesIconSVG,
    show: isAdmin,
    isActive: (pathname: string) => pathname.includes("/statistics"),
  },
  {
    label: "Paramètres",
    path: "/settings",
    icon: SidebarIcon.SettingsSVG,
    show: isAdmin,
    isActive: (pathname: string) => pathname.includes("/settings"),
  },
];
// const createMiscellaneousRoutes = (user: User | null, isAdmin: boolean) => [
// {
//   label: "Transactions",
//   path: "/transactions",
//   icon: SidebarIcon.ArrowsRightLeftIcon,
//   show: isAdmin,
//   isActive: (pathname: string) => pathname.includes("/transactions")
// }
// ];

interface SidebarProps {
  onClose?: () => void;
  className?: string;
}

enum Sidebars {
  ADMIN = "ADMIN",
  PLACE_ADMIN = "PLACE_ADMIN",
}

export default function Sidebar(props: SidebarProps) {
  const { onClose, className = "" } = props;

  const { isAdmin, user } = useAuth();
  const { hasNew, markSeen } = useNewDataAlerts(isAdmin);
  const location = useLocation();
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const searchQuery = useDebounce(searchTerm, 300);
  const { selectedPlace } = useSelectedPlace(user, isAdmin);
  const { total: requestTotal, hasNew: hasNewRequests } =
    usePartnerRequestAlerts(isAdmin);

  const adminRoutes = createDashboardRoutes(
    user,
    isAdmin,
    selectedPlace?.isEventsEnabled,
  );

  const menus = useMemo(
    () => [
      { title: "Tableau de bord", menus: adminRoutes },
      { title: "Venez Taxi", menus: createVenezTaxiRoutes(user, isAdmin) },
      // {
      //   title: "Miscellaneous",
      //   menus: createMiscellaneousRoutes(user, isAdmin)
      // }
    ],
    [adminRoutes, user, isAdmin],
  );

  const filteredMenus = useMemo(() => {
    if (!searchQuery) return menus;
    return menus
      .map((section) => ({
        ...section,
        menus: section.menus.filter((menu) =>
          menu.label.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      }))
      .filter((section) => section.menus.length > 0);
  }, [menus, searchQuery]);

  // Determine which accordion should be open based on the current route
  // useEffect(() => {
  //   const updateActiveAccordion = () => {
  //     if (searchQuery) {
  //       const matchIndex = filteredMenus.findIndex(
  //         (section) => section.menus.length > 0,
  //       );
  //       setActiveAccordion(matchIndex !== -1 ? matchIndex : 0);
  //     } else {
  //       const currentMenuIndex = menus.findIndex((menu) =>
  //         menu.menus.some((item) => item.isActive(location.pathname)),
  //       );
  //       setActiveAccordion(currentMenuIndex !== -1 ? currentMenuIndex : 0);
  //     }
  //   };

  //   updateActiveAccordion();
  // }, [location.pathname, menus, filteredMenus, searchQuery]);

  useEffect(() => {
    const updateActiveAccordion = () => {
      if (searchQuery) {
        const matchIndex = filteredMenus.findIndex(
          (section) => section.menus.length > 0,
        );
        // Use null if no match found
        setActiveAccordion(matchIndex !== -1 ? matchIndex : null);
      } else {
        const currentMenuIndex = menus.findIndex((menu) =>
          menu.menus.some((item) => item.isActive(location.pathname)),
        );
        // Use null if no match found
        setActiveAccordion(currentMenuIndex !== -1 ? currentMenuIndex : null);
      }
    };

    updateActiveAccordion();
  }, [location.pathname, menus, filteredMenus, searchQuery]);

  useEffect(() => {
    menus.forEach((section) =>
      section.menus.forEach((item) => {
        if (item.isActive(location.pathname)) markSeen(item.path);
      }),
    );
  }, [location.pathname, markSeen, menus]);

  const activeSidebar = isAdmin ? Sidebars.ADMIN : Sidebars.PLACE_ADMIN;

  const SidebarMenu = {
    [Sidebars.ADMIN]: () => (
      <div>
        <div className="px-2 mb-3">
          <input
            type="search"
            className="custom-input"
            name="search"
            placeholder="Rechercher"
            value={searchTerm}
            autoFocus={searchQuery ? true : false}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
          />
        </div>
        <Link
          to="/home"
          className={`border-bottom border-black ${
            location.pathname.includes("/home") ? "text-danger" : "text-black"
          } d-block w-100`}
        >
          <div className="d-flex w-full fw-medium gap-2 px-4 py-3 sidbar-link">
            {React.createElement(SidebarIcon.HomeSvg, {
              className: `${
                [
                  "variables",
                  "promo codes",
                  "transactions",
                  "special zones",
                ].includes("accueil")
                  ? "w-5 h-5"
                  : ""
              } ${
                location.pathname.includes("/home")
                  ? "fill-danger"
                  : "fill-black"
              }`,
            })}
            Accueil
          </div>
        </Link>
        <CAccordion activeItemKey={activeAccordion!} flush>
          {filteredMenus.map((accordion, index) => (
            <CAccordionItem itemKey={index} key={index}>
              <CAccordionHeader>{accordion.title}</CAccordionHeader>
              <CAccordionBody className="nav-menu">
                {accordion.menus.map((menu) =>
                  menu.show ? (
                    <CNavItem
                      onClick={onClose}
                      href={menu.path}
                      className={`list-unstyled ${
                        menu.isActive(location.pathname)
                          ? "text-danger"
                          : "text-black"
                      }`}
                      key={menu.label}
                    >
                      <Link
                        to={menu.path}
                        className={`${
                          menu.isActive(location.pathname)
                            ? "text-danger"
                            : "text-black"
                        } w-full`}
                      >
                        <div
                          className="d-flex align-items-center w-100 fw-medium gap-3 sidbar-link"
                          style={{ minWidth: 0 }}
                        >
                          <span
                            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 24, height: 24 }}
                          >
                            {React.createElement(menu.icon, {
                              className: `w-100 h-100 ${
                                menu.isActive(location.pathname)
                                  ? "fill-danger"
                                  : "fill-black"
                              }`,
                            })}
                          </span>

                          <span className="flex-grow-1" style={{ minWidth: 0 }}>
                            {menu.label}
                          </span>

                          {menu.path === "/partners" ? (
                            <PartnerRequestBadge
                              total={requestTotal}
                              hasNew={hasNewRequests}
                            />
                          ) : (
                            hasNew(menu.path) && <NewDataBadge />
                          )}
                        </div>
                      </Link>
                    </CNavItem>
                  ) : null,
                )}
              </CAccordionBody>
            </CAccordionItem>
          ))}
        </CAccordion>
      </div>
    ),
    [Sidebars.PLACE_ADMIN]: () => (
      <>
        {adminRoutes.map((menu) =>
          menu.show ? (
            <CNavItem
              onClick={onClose}
              href={menu.path}
              className={`list-unstyled ${
                menu.isActive(location.pathname) ? "text-danger" : "text-black"
              }`}
              key={menu.label}
            >
              <Link
                to={menu.path}
                className={`${
                  menu.isActive(location.pathname)
                    ? "text-danger"
                    : "text-black"
                } w-full`}
              >
                <div className="d-flex w-full fw-medium gap-3 sidbar-link">
                  {React.createElement(menu.icon, {
                    className: `flex-shrink-0 ${
                      [
                        "variables",
                        "promo codes",
                        "transactions",
                        "special zones",
                      ].includes(menu.label.toLowerCase())
                        ? "w-5 h-5"
                        : ""
                    } ${menu.isActive(location.pathname) ? "fill-danger" : "fill-black"}`,
                  })}
                  {menu.label}
                </div>
              </Link>
            </CNavItem>
          ) : null,
        )}
      </>
    ),
  }[activeSidebar];

  return (
    <div className={`sidebar px-1 py-3 ${className}`}>
      <SidebarMenu />
    </div>
  );
}
